import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import goToApp from '../utils/goToApp';
import './TeacherFreeTier.css';

/**
 * StudentJoinPage — /class-join
 *
 * Public, no-auth entry point for a student to join a free-tier teacher's
 * class via 6-character class code. Mirrors the flow described in
 * docs/FREE_TEACHER_TIER_SCOPE.md §Day 4.
 *
 * Student enters code + first name. Backend mints a minimal-PII student
 * account (no email, no last name) and drops an auth cookie. We redirect
 * straight into a Mira voice session.
 */

const CODE_RE = /^[A-Z2-9]{6}$/;

const StudentJoinPage = () => {
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [inviteToken, setInviteToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [classPreview, setClassPreview] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = (params.get('code') || '').toUpperCase();
    const tokenParam = params.get('invite');
    if (codeParam) setCode(codeParam.slice(0, 6));
    if (tokenParam) setInviteToken(tokenParam);
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cleanCode = code.trim().toUpperCase();
    const cleanName = firstName.trim();

    if (!CODE_RE.test(cleanCode)) {
      setError('Class codes are 6 letters/numbers (no 0, O, 1, or I).');
      return;
    }
    if (!cleanName || cleanName.length < 1 || cleanName.length > 30) {
      setError('Enter your first name (up to 30 letters).');
      return;
    }
    if (!/^[A-Za-z][A-Za-z\-'\s]{0,30}$/.test(cleanName)) {
      setError('First name must be letters only.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        code: cleanCode,
        first_name: cleanName,
      };
      if (inviteToken) payload.invite_token = inviteToken;

      const response = await axios.post('/api/classroom/join', payload);
      const { user } = response.data || {};

      if (user) {
        localStorage.setItem('user_info', JSON.stringify(user));
        setClassPreview({
          className: user.class_name,
          firstName: user.name,
        });
        setTimeout(() => {
          // Hard hop to the app host — a soft navigate() on the apex domain
          // hits the landing-page catch-all, and even on app.* the running
          // bundle still has logged-out state (field-reported: student joins
          // but stays stuck on this page / lands on the marketing page).
          goToApp('/tutor?source=class_join');
        }, 1200);
      } else {
        setError('Joined, but we could not start your session. Refresh and try again.');
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error;
      if (code === 'class_full') {
        setError(msg || 'This class is full. Ask your teacher.');
      } else if (err.response?.status === 404) {
        setError("We couldn't find that class code. Double-check with your teacher.");
      } else if (err.response?.status === 410) {
        setError(msg || 'This class code has expired.');
      } else {
        setError(msg || 'Something went wrong. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (classPreview) {
    return (
      <div className="teacher-onboarding-page">
        <div className="teacher-onboarding-card" style={{ textAlign: 'center' }}>
          <div className="teacher-step-badge">You're in</div>
          <h2 className="teacher-step-title">
            Welcome, {classPreview.firstName}.
          </h2>
          <p className="teacher-step-body">
            Starting your Mira session…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-onboarding-page">
      <div className="teacher-onboarding-card">
        <div className="teacher-step-badge">Join class</div>
        <h2 className="teacher-step-title">Join your teacher's class.</h2>
        <p className="teacher-step-body">
          Enter the 6-character code your teacher shared and your first name.
          No email or password needed.
        </p>

        <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
          <label
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#6b7280',
              marginBottom: 6,
            }}
            htmlFor="class-code"
          >
            Class code
          </label>
          <input
            id="class-code"
            className="teacher-input"
            type="text"
            value={code}
            maxLength={6}
            onChange={(e) =>
              setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))
            }
            placeholder="E.g. 4B9T2X"
            autoComplete="off"
            style={{
              fontFamily: "'SF Mono', Menlo, monospace",
              fontSize: 20,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: 16,
            }}
            autoFocus={!code}
          />

          <label
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#6b7280',
              marginBottom: 6,
            }}
            htmlFor="first-name"
          >
            First name
          </label>
          <input
            id="first-name"
            className="teacher-input"
            type="text"
            value={firstName}
            maxLength={30}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="E.g. Alex"
            autoComplete="given-name"
            autoFocus={Boolean(code)}
          />

          {error && <div className="teacher-inline-error">{error}</div>}

          <div className="teacher-step-actions" style={{ marginTop: 20 }}>
            <button
              type="submit"
              className="teacher-btn-primary"
              disabled={loading}
            >
              {loading ? 'Joining…' : 'Join class →'}
            </button>
          </div>

          <p
            className="teacher-lite-empty-hint"
            style={{ marginTop: 18, textAlign: 'center' }}
          >
            By joining, you agree to Tappy's terms. Your teacher is the
            authorized adult for this account.
          </p>
        </form>
      </div>
    </div>
  );
};

export default StudentJoinPage;
